import { themes } from 'kb-shared';
import Honeybadger from '@honeybadger-io/js';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Loader from '../../../components/Loader/Loader';
import WhiteSquare, { size } from '../../../components/WhiteSquare';
import { showErrorToast } from '../../../utilities/notificationUtils';
import {
  isValidFileExtension,
  showErrorValidExtensionToast
} from '../../../utilities/fileValidation';
import { AttachmentInformation } from '../types';
import AttachmentsList from './AttachmentsList';
import { uploadFile, CATEGORY } from '../../../api/pf2/files';

const { fonts, colors, breakpoints } = themes;
interface PatientUploadResponse {
  id: string;
}

interface PatientUploadVariables {
  fileName: string;
  uuid: string;
}

interface ContainerProps {
  hasItems?: boolean;
}

const Container = styled.div`
  width: 100%;
  margin-top: 20px;
  margin-bottom: ${({ hasItems }: ContainerProps) => (hasItems ? '0' : '10px')};
`;

const TopContent = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;

  @media (max-width: ${breakpoints.tabletPortrait}) {
    padding: 20px;
    flex-direction: column;
  }
`;

const UploadContent = styled.div`
  display: flex;
  flex-flow: column wrap;
`;

const Title = styled.h2`
  ${({ theme }) => theme.typos.sectionHeader};
  display: flex;
`;

const Subtitle = styled.p`
  font-size: 20px;
  display: flex;
  ${fonts.FoundersGrotesk};
  margin: 20px 0;
`;

const FileUploadContainer = styled.div`
  width: ${size}px;
  margin-bottom: 28px;

  @media (max-width: ${breakpoints.tabletPortrait}) {
    align-self: center;
    margin-top: 15px;
  }
`;

const FileUpload = styled.input`
  cursor: pointer;
  position: absolute;
  height: ${size}px;
  opacity: 0;
  width: ${size}px;
`;

const LoadingContainer = styled.div`
  align-items: center;
  display: flex;
  height: ${size}px;
  width: ${size}px;

  .loader {
    margin-top: 30px;
  }
`;

const FileUploadDescription = styled.small`
  ${themes.fonts.FoundersGrotesk};
  max-width: 140px;
  display: inline-block;
  text-align: center;
  margin: 8px 0 0 0;
`;

type Props = {
  items: AttachmentInformation[];
  onUpload: (success: boolean) => void;
  onDelete: () => void;
};

export const YourUploads = (props: Props) => {
  const { t } = useTranslation();
  const MAX_MEGABYTES = 15;
  const { items, onUpload, onDelete } = props;
  const [loading, setLoading] = useState(false);

  const handleChange = async e => {
    e.preventDefault();
    const {
      target: { files }
    } = e;

    if (!files || !files[0]) {
      return;
    }

    if (!isValidFileExtension(files[0].type)) {
      return showErrorValidExtensionToast();
    }

    const maxSize = 1048576 * MAX_MEGABYTES; // 1mb = 1048576
    if (files[0].size > maxSize) {
      showErrorToast(
        `Error uploading file. Your file is too big. Please upload a file less than ${MAX_MEGABYTES}mb. Please contact navigator@kindbody.com if you need assistance.`
      );
      return;
    }

    try {
      setLoading(true);
      await uploadFile(files[0], CATEGORY.DOCUMENT);
      onUpload(true);
      setLoading(false);
    } catch (err) {
      Honeybadger.notify(err, { name: 'DocumentUploadError' });
    }
  };

  return (
    <Container hasItems={items.length > 0}>
      <TopContent>
        <UploadContent>
          <Title>Upload a new document</Title>
          <Subtitle>Drag a document into the dotted box or click to select the file</Subtitle>
        </UploadContent>
        <FileUploadContainer>
          <FileUpload type="file" accept=".jpeg, .jpg, .png, .pdf" onChange={handleChange} />
          {loading ? (
            <LoadingContainer>
              <Loader type="spin" color={colors.KBWhite} height={size} width={size} />
            </LoadingContainer>
          ) : (
            <WhiteSquare />
          )}
          <FileUploadDescription>
            {t('upload_button.help_text', { megabytes: MAX_MEGABYTES })}
          </FileUploadDescription>
        </FileUploadContainer>
      </TopContent>
      {items.length > 0 && (
        <AttachmentsList
          isPatientAttachments
          hasHeader
          properties={['date']}
          items={items}
          onDelete={onDelete}
        />
      )}
    </Container>
  );
};
